import { useState, useEffect, useCallback } from 'react';
import { Music, Plus, Search, Edit3, Upload, Trash2, Play, ChevronRight, X, Archive } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { SundayWorshipSection, WORSHIP_SLOTS } from '../components/music/SundayWorshipSection';
import { PrintSetlistDialog } from '../components/music/PrintSetlistDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const FONT = { fontFamily: "'Crimson Text', Georgia, serif" };
const TAGS = ["praise", "worship", "devotion", "selection", "mastered", "christmas", "black history", "resurrection", "offering", "greeting", "prayer"];

const PasswordGate = ({ open, onClose, onSuccess, label }) => {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const submit = () => { onSuccess(pw); setPw(''); setErr(''); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setPw(''); setErr(''); } }}>
      <DialogContent className="sm:max-w-sm" data-testid="password-gate">
        <DialogHeader>
          <DialogTitle className="text-xl text-blue-800">{label}</DialogTitle>
          <DialogDescription style={FONT}>Enter the password to continue.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input type="password" data-testid="password-input" value={pw} onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Password" className="border-2 border-blue-200" style={FONT} />
          {err && <p className="text-red-500 text-sm mt-1" style={FONT}>{err}</p>}
        </div>
        <DialogFooter>
          <Button data-testid="password-submit" onClick={submit} className="bg-blue-700 hover:bg-blue-800 text-white" style={FONT}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SongEditDialog = ({ open, onClose, song, password, onSaved }) => {
  const [form, setForm] = useState({ title: '', author: '', song_key: '', lyrics: '', tags: [], aliases: [] });
  const [newAlias, setNewAlias] = useState('');
  const [saving, setSaving] = useState(false);
  const isNew = !song;

  useEffect(() => {
    if (song) setForm({ title: song.title, author: song.author, song_key: song.song_key, lyrics: song.lyrics, tags: song.tags || [], aliases: song.aliases || [] });
    else setForm({ title: '', author: '', song_key: '', lyrics: '', tags: [], aliases: [] });
  }, [song, open]);

  const toggleTag = (tag) => setForm(f => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag] }));
  const addAlias = () => { if (newAlias.trim()) { setForm(f => ({ ...f, aliases: [...f.aliases, newAlias.trim()] })); setNewAlias(''); } };
  const removeAlias = (a) => setForm(f => ({ ...f, aliases: f.aliases.filter(x => x !== a) }));

  const save = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/api/songs` : `${API}/api/songs/${song.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, password }) });
      if (res.ok) { toast.success(isNew ? 'Song added!' : 'Song updated!'); onSaved(); onClose(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Connection error'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="song-edit-dialog">
        <DialogHeader><DialogTitle className="text-xl text-blue-800">{isNew ? 'Add Song' : 'Edit Song'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label style={FONT} className="text-blue-800 font-semibold">Song Title *</Label>
            <Input data-testid="song-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 border-2 border-blue-200" style={FONT} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label style={FONT} className="text-blue-800 font-semibold">Original Author</Label>
              <Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="mt-1 border-2 border-blue-200" style={FONT} /></div>
            <div><Label style={FONT} className="text-blue-800 font-semibold">Key</Label>
              <Input value={form.song_key} onChange={e => setForm(f => ({ ...f, song_key: e.target.value }))} placeholder="e.g. C, G, Em" className="mt-1 border-2 border-blue-200" style={FONT} /></div>
          </div>
          <div><Label style={FONT} className="text-blue-800 font-semibold">Lyrics</Label>
            <Textarea value={form.lyrics} onChange={e => setForm(f => ({ ...f, lyrics: e.target.value }))} rows={8} className="mt-1 border-2 border-blue-200 font-mono text-sm" /></div>
          <div><Label style={FONT} className="text-blue-800 font-semibold">Tags</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {TAGS.map(tag => (
                <Badge key={tag} variant={form.tags.includes(tag) ? "default" : "outline"} className={`cursor-pointer capitalize ${form.tags.includes(tag) ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700'}`}
                  onClick={() => toggleTag(tag)} style={FONT}>{tag}</Badge>
              ))}
            </div></div>
          <div><Label style={FONT} className="text-blue-800 font-semibold">Aliases</Label>
            <div className="flex gap-2 mt-1">
              <Input value={newAlias} onChange={e => setNewAlias(e.target.value)} onKeyDown={e => e.key === 'Enter' && addAlias()} placeholder="Add alias name" className="border-2 border-blue-200 flex-1" style={FONT} />
              <Button onClick={addAlias} variant="outline" className="border-blue-300">Add</Button>
            </div>
            {form.aliases.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.aliases.map(a => (
                  <Badge key={a} variant="secondary" className="bg-yellow-100 text-blue-800 gap-1" style={FONT}>
                    {a} <X className="h-3 w-3 cursor-pointer" onClick={() => removeAlias(a)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="song-save-btn" onClick={save} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UploadDialog = ({ open, onClose, song, password, onSaved }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); if (!name) setName(f.name.replace(/\.[^.]+$/, '')); }
  };

  const save = async () => {
    if (!file || !name.trim()) { toast.error('Select a file and enter a name'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      fd.append('password', password);
      const res = await fetch(`${API}/api/songs/${song.id}/recordings`, { method: 'POST', body: fd });
      if (res.ok) { toast.success('Audio uploaded!'); onSaved(); reset(); onClose(); }
      else { const e = await res.json(); toast.error(e.detail || 'Upload failed'); }
    } catch { toast.error('Upload failed'); } finally { setSaving(false); }
  };

  const reset = () => { setFile(null); setName(''); };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
        <DialogHeader><DialogTitle className="text-xl text-blue-800">Upload Audio - {song?.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label style={FONT} className="text-blue-800 font-semibold">Audio File</Label>
            <Input type="file" accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.mp4,.aac" onChange={handleFileChange}
              className="mt-1 border-2 border-blue-200 cursor-pointer" data-testid="audio-file-input" />
          </div>
          {file && (
            <div className="space-y-3">
              <p className="text-sm text-blue-600" style={FONT}>Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</p>
              <audio src={URL.createObjectURL(file)} controls className="w-full" />
              <div>
                <Label style={FONT} className="text-blue-800 font-semibold">Recording Name</Label>
                <Input data-testid="upload-name-input" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Name this recording" className="mt-1 border-2 border-blue-200" style={FONT} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="save-upload-btn" onClick={save} disabled={saving || !file}
            className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT}>
            {saving ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SongDetail = ({ song, allSongs, onClose, onEdit, onRecord, password, onRefresh }) => {
  const [recordings, setRecordings] = useState([]);

  const fetchRecs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/songs/${song.id}/recordings`);
      if (res.ok) setRecordings(await res.json());
    } catch { toast.error('Unable to load recordings'); }
  }, [song.id]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  const deleteRec = async (rec) => {
    if (!password) return;
    try {
      const res = await fetch(`${API}/api/recordings/${rec.id}?password=${password}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchRecs(); } else toast.error('Failed');
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <button onClick={onClose} className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1" style={FONT} data-testid="back-to-list">
        <ChevronRight className="h-4 w-4 rotate-180" /> Back to song list
      </button>
      <div>
        <h2 className="text-5xl md:text-6xl font-bold text-blue-800 mb-2">{song.title}</h2>
        {song.author && <p className="text-lg text-blue-700" style={FONT}>By {song.author}</p>}
        {song.song_key && <p className="text-lg text-yellow-700 font-semibold" style={FONT}>Key: {song.song_key}</p>}
        {song.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {song.tags.map(t => <Badge key={t} className="bg-blue-100 text-blue-800 capitalize" style={FONT}>{t}</Badge>)}
          </div>
        )}
        {song.aliases?.length > 0 && (
          <p className="text-blue-600 text-sm mt-2" style={FONT}>Also known as: {song.aliases.join(', ')}</p>
        )}
      </div>
      {song.lyrics && (
        <Card className="border-2 border-blue-200">
          <CardContent className="p-6">
            <h3 className="text-3xl font-bold text-blue-800 mb-3">Lyrics</h3>
            <pre className="whitespace-pre-wrap text-blue-900 text-2xl leading-relaxed" style={FONT}>{song.lyrics}</pre>
          </CardContent>
        </Card>
      )}
      {/* Recordings */}
      <Card className="border-2 border-yellow-300">
        <CardContent className="p-6">
          <h3 className="text-3xl font-bold text-blue-800 mb-3">Recordings</h3>
          {recordings.length === 0 ? (
            <p className="text-blue-600" style={FONT}>No recordings yet.</p>
          ) : (
            <div className="space-y-3">
              {recordings.map(rec => (
                <div key={rec.id} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Play className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-blue-800 text-sm" style={FONT}>{rec.name}</p>
                    <audio src={`${API}/api/recordings/audio/${rec.filename}`} controls className="w-full mt-1 h-8" />
                  </div>
                  {password && (
                    <button onClick={() => deleteRec(rec)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-rec-${rec.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex gap-3">
        <Button onClick={onEdit} className="bg-blue-600 hover:bg-blue-700 text-white" style={FONT} data-testid="edit-song-btn">
          <Edit3 className="h-4 w-4 mr-2" /> Edit
        </Button>
        <Button onClick={() => onRecord()} className="bg-blue-500 hover:bg-blue-600 text-white" style={FONT} data-testid="upload-song-btn">
          <Upload className="h-4 w-4 mr-2" /> Upload Audio
        </Button>
      </div>
    </div>
  );
};

export const MusicMinistry = () => {
  const [songs, setSongs] = useState([]);
  const [worship, setWorship] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSong, setSelectedSong] = useState(null);
  const [musicPw, setMusicPw] = useState('');

  const [pwGateOpen, setPwGateOpen] = useState(false);
  const [pwAction, setPwAction] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSong, setEditSong] = useState(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [worshipEditOpen, setWorshipEditOpen] = useState(false);
  const [worshipDraft, setWorshipDraft] = useState([]);
  const [setlistOpen, setSetlistOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiveDate, setArchiveDate] = useState('');
  const fetchSongs = useCallback(async () => {
    try { const r = await fetch(`${API}/api/songs`); if (r.ok) setSongs(await r.json()); } catch { toast.error('Unable to load songs'); }
  }, []);
  const fetchWorship = useCallback(async () => {
    try { const r = await fetch(`${API}/api/sunday-worship`); if (r.ok) { const d = await r.json(); setWorship(d.song_ids || []); } } catch { toast.error('Unable to load worship list'); }
  }, []);

  useEffect(() => { fetchSongs(); fetchWorship(); }, [fetchSongs, fetchWorship]);

  const requirePw = (action) => {
    if (musicPw) { action(musicPw); return; }
    setPwAction(() => action);
    setPwGateOpen(true);
  };

  const handlePwSuccess = (pw) => {
    setMusicPw(pw);
    setPwGateOpen(false);
    if (pwAction) pwAction(pw);
  };

  const handleAddSong = () => requirePw(() => { setEditSong(null); setEditDialogOpen(true); });
  const handleEditSong = () => requirePw(() => { setEditSong(selectedSong); setEditDialogOpen(true); });
  const handleUploadSong = () => requirePw(() => setRecordDialogOpen(true));
  const handleEditWorship = () => requirePw(() => { setWorshipDraft([...worship]); setWorshipEditOpen(true); });

  const handleArchiveThis = () => requirePw(() => {
    const nextSunday = new Date();
    const day = nextSunday.getDay();
    nextSunday.setDate(nextSunday.getDate() + (day === 0 ? 0 : 7 - day));
    setArchiveDate(nextSunday.toISOString().split('T')[0]);
    setArchiveConfirmOpen(true);
  });

  const submitArchive = async () => {
    try {
      const r = await fetch(`${API}/api/worship-archive/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: archiveDate, password: musicPw }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(data.message);
        setArchiveConfirmOpen(false);
        fetchArchives();
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed');
      }
    } catch { toast.error('Connection error'); }
  };

  const fetchArchives = useCallback(async () => {
    try { const r = await fetch(`${API}/api/worship-archive`); if (r.ok) setArchives(await r.json()); } catch { toast.error('Unable to load archives'); }
  }, []);

  const saveWorship = async () => {
    try {
      const r = await fetch(`${API}/api/sunday-worship`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ song_ids: worshipDraft, password: musicPw }),
      });
      if (r.ok) { toast.success('Worship list updated!'); fetchWorship(); setWorshipEditOpen(false); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); setMusicPw(''); }
    } catch { toast.error('Error'); }
  };

  // Build display list with aliases
  const buildDisplayList = () => {
    const items = [];
    const filtered = songs.filter(s => {
      if (selectedTags.length === 0) return true;
      return selectedTags.some(t => s.tags?.includes(t));
    }).filter(s => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return s.title.toLowerCase().includes(q) || s.aliases?.some(a => a.toLowerCase().includes(q));
    });

    for (const song of filtered) {
      items.push({ type: 'song', song, sortKey: song.title.toLowerCase() });
      for (const alias of (song.aliases || [])) {
        if (!searchQuery || alias.toLowerCase().includes(searchQuery.toLowerCase())) {
          items.push({ type: 'alias', alias, song, sortKey: alias.toLowerCase() });
        }
      }
    }
    return items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  };

  const displayList = buildDisplayList();
  const worshipSongs = worship.map(id => songs.find(s => s.id === id) || null);

  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  if (selectedSong) {
    return (
      <div className="min-h-screen bg-white">
        <section className="relative h-[300px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800" />
          <div className="relative z-10 text-center"><Music className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-6xl md:text-7xl font-bold text-white">Music Ministry</h1></div>
        </section>
        <section className="py-12 max-w-4xl mx-auto px-4">
          <SongDetail song={selectedSong} allSongs={songs} onClose={() => setSelectedSong(null)}
            onEdit={handleEditSong} onRecord={handleUploadSong} password={musicPw} onRefresh={fetchSongs} />
        </section>
        <PasswordGate open={pwGateOpen} onClose={() => setPwGateOpen(false)} onSuccess={handlePwSuccess} label="Music Ministry Access" />
        <SongEditDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} song={editSong} password={musicPw} onSaved={() => { fetchSongs(); if (editSong) setSelectedSong(prev => ({ ...prev })); }} />
        <UploadDialog open={recordDialogOpen} onClose={() => setRecordDialogOpen(false)} song={selectedSong} password={musicPw} onSaved={fetchSongs} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <Music className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-4">Music Ministry</h1>
          <p className="text-2xl text-yellow-300">Lifting voices in praise and worship</p>
        </div>
      </section>

      {/* Sunday Worship */}
      <SundayWorshipSection
        worshipSongs={worshipSongs}
        onArchiveView={() => { fetchArchives(); setArchiveOpen(true); }}
        onArchiveThis={handleArchiveThis}
        onPrintSetlist={() => setSetlistOpen(true)}
        onEdit={handleEditWorship}
        onSongClick={setSelectedSong}
      />

      {/* Tag Filters + Song List */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-5xl font-bold text-blue-800">Song Library</h2>
            <Button onClick={handleAddSong} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT} data-testid="add-song-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Song
            </Button>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="tag-filter-bar">
            {TAGS.map(tag => (
              <Badge key={tag} variant={selectedTags.includes(tag) ? "default" : "outline"}
                className={`cursor-pointer capitalize text-sm px-3 py-1 ${selectedTags.includes(tag) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                onClick={() => toggleTag(tag)} style={FONT}>{tag}</Badge>
            ))}
            {selectedTags.length > 0 && (
              <Badge variant="outline" className="cursor-pointer border-red-300 text-red-600 hover:bg-red-50" onClick={() => setSelectedTags([])} style={FONT}>Clear All</Badge>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search songs..."
              className="pl-10 border-2 border-blue-200" style={FONT} data-testid="song-search" />
          </div>

          {/* Song List */}
          {displayList.length === 0 ? (
            <p className="text-blue-600 text-center py-12" style={FONT}>No songs found. Add your first song!</p>
          ) : (
            <div className="space-y-1">
              {displayList.map((item) => (
                item.type === 'song' ? (
                  <div key={item.song.id} onClick={() => setSelectedSong(item.song)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 cursor-pointer border border-transparent hover:border-blue-200 transition-colors" data-testid={`song-item-${item.song.id}`}>
                    <Music className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    <span className="font-semibold text-blue-800" style={FONT}>{item.song.title}</span>
                    {item.song.song_key && <span className="text-yellow-700 text-sm ml-auto" style={FONT}>{item.song.song_key}</span>}
                  </div>
                ) : (
                  <div key={`alias-${item.alias}`} onClick={() => setSelectedSong(item.song)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-yellow-50 cursor-pointer border border-transparent hover:border-yellow-200 transition-colors" data-testid={`alias-item-${item.alias}`}>
                    <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <span className="text-blue-600" style={FONT}>{item.alias}</span>
                    <span className="text-blue-400 text-sm" style={FONT}>— See &quot;{item.song.title}&quot;</span>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Dialogs */}
      <PasswordGate open={pwGateOpen} onClose={() => setPwGateOpen(false)} onSuccess={handlePwSuccess} label="Music Ministry Access" />
      <SongEditDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} song={editSong} password={musicPw} onSaved={fetchSongs} />

      {/* Worship Edit Dialog */}
      <Dialog open={worshipEditOpen} onOpenChange={o => !o && setWorshipEditOpen(false)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="worship-edit-dialog">
          <DialogHeader><DialogTitle className="text-xl text-blue-800">Edit Sunday Worship Songs</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {WORSHIP_SLOTS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-yellow-600 font-bold text-xs w-20 text-right flex-shrink-0" style={FONT}>{label}</span>
                <select value={worshipDraft[i] || ''} onChange={e => {
                  const next = [...worshipDraft];
                  next[i] = e.target.value || '';
                  setWorshipDraft(next);
                }} className="flex-1 border-2 border-blue-200 rounded-md px-3 py-2 text-sm" style={FONT}>
                  <option value="">-- None --</option>
                  {songs.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={saveWorship} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT} data-testid="save-worship-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintSetlistDialog open={setlistOpen} onClose={() => setSetlistOpen(false)} worshipSongs={worshipSongs} />

      {/* Archive Dialog */}
      <Dialog open={archiveOpen} onOpenChange={o => !o && setArchiveOpen(false)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="archive-dialog">
          <DialogHeader><DialogTitle className="text-xl text-blue-800">Worship Song Archive</DialogTitle></DialogHeader>
          <div className="py-2">
            {archives.length === 0 ? (
              <p className="text-blue-600 text-center py-8" style={FONT}>No archived setlists yet. Archives are saved each time the worship list is updated.</p>
            ) : (
              <div className="space-y-4">
                {archives.map(entry => (
                  <Card key={entry.id} className="border border-blue-200">
                    <CardContent className="p-4">
                      <p className="font-bold text-blue-800 mb-2" style={FONT}>
                        {entry.label || new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      <div className="space-y-1">
                        {entry.song_titles.map((title, i) => (
                          <div key={`${entry.id}-${i}`} className="flex items-center gap-2">
                            <span className="text-yellow-600 font-bold text-xs w-20 text-right flex-shrink-0" style={FONT}>{WORSHIP_SLOTS[i] || `Song ${i + 1}`}</span>
                            <span className="text-blue-700 text-sm" style={FONT}>{title}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirm Dialog */}
      <Dialog open={archiveConfirmOpen} onOpenChange={o => !o && setArchiveConfirmOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-800">Archive This Setlist</DialogTitle>
            <DialogDescription style={FONT}>
              Confirm the date for this setlist. The current worship songs will be saved to the archive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label style={FONT} className="text-blue-800 font-semibold">Date for this setlist</Label>
              <Input type="date" value={archiveDate} onChange={e => setArchiveDate(e.target.value)}
                className="mt-1 border-2 border-blue-200" style={FONT} data-testid="archive-date-input" />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="font-semibold text-blue-800 text-sm mb-2" style={FONT}>Songs to archive:</p>
              {WORSHIP_SLOTS.map((label, i) => (
                <p key={label} className="text-blue-700 text-sm" style={FONT}>{label}: {worshipSongs[i]?.title || '—'}</p>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveConfirmOpen(false)} style={FONT}>Cancel</Button>
            <Button onClick={submitArchive} className="bg-green-600 hover:bg-green-700 text-white font-bold" style={FONT} data-testid="archive-confirm-btn">
              Confirm &amp; Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
